import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";

import MarketingFooter from "@/components/MarketingFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { H1, H2 } from "@/components/ui/typography";
import { getCurrentUser, checkAuth, login } from "@/lib/api";
import { productName } from "@/lib/constants";
import { removeInvisibleChars } from "@/lib/remove-invisible-chars";

export const Route = createFileRoute("/login-old")({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    // validate and parse the search params into a typed state
    return {
      redirect: search?.redirect as string | undefined,
    };
  },
  beforeLoad: async () => {
    let isLoggedIn = false;

    try {
      await checkAuth();
      await getCurrentUser();
      isLoggedIn = true;
    } catch {
      // not logged in
    }

    if (isLoggedIn) {
      throw redirect({ to: "/app" });
    }
  },
  head: () => ({ meta: [{ title: "Login" }] }),
});

function LoginPage() {
  const navigate = useNavigate({ from: "/login" });
  const router = useRouter();
  const { redirect } = Route.useSearch() || {};

  const mutation = useMutation({
    mutationFn: async (e: React.FormEvent<HTMLFormElement>) => {
      const form = new FormData(e.target as HTMLFormElement);
      const username = removeInvisibleChars(
        (form.get("username") as string).replace(/^@/, ""),
      );
      const password = removeInvisibleChars(form.get("password") as string);
      const domain = removeInvisibleChars(
        form.get("service_domain") as string,
      ).replace(/https?:\/\//, "");

      await login(username, password, domain);
    },
    onSuccess: async () => {
      if (redirect) {
        router.history.push(redirect);
      } else {
        await navigate({ to: "/app" });
      }
    },
  });

  return (
    <main className="flex flex-col min-h-screen">
      <div className="flex-1 flex justify-center bg-slate-subtle pt-[5%]">
        <div className="px-4 flex flex-col gap-4  text-slate-normal">
          <H1 className="text-center p-6">{productName}</H1>
          <div className="flex-1 flex flex-col items-center pb-20">
            <form
              className="border border-slate-7 dark:border-slatedark-7 rounded-md py-8 px-6 max-w-md w-full flex flex-col gap-4 bg-slate-app"
              onSubmit={(e) => {
                e.preventDefault();
                mutation.mutate(e);
              }}
            >
              <H2 className="border-none">Login with Bluesky</H2>
              <div className="flex flex-col gap-4">
                <SimpleTooltip
                  side="left"
                  message="Where your Bluesky account is hosted. If you don't know, leave this as is."
                >
                  <Input
                    placeholder="PDS Host"
                    name="service_domain"
                    defaultValue="bsky.social"
                  />
                </SimpleTooltip>
                <Input
                  placeholder="user.bsky.social"
                  name="username"
                  autoComplete="username"
                  ignore1p={false}
                />
                <Input
                  placeholder="Bluesky App Password"
                  ignore1p={false}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  onInput={(e) => {
                    if (e.currentTarget.value.match(/.{4}-.{4}-.{4}-.{4}/)) {
                      e.currentTarget.setCustomValidity("");
                    } else {
                      e.currentTarget.setCustomValidity(
                        "That doesn't look like an app password. Make sure you aren't using your account password. It should look somethings like this: abcd-1234-abcd-1234",
                      );
                    }
                  }}
                />
              </div>
              <p className="text-sm text-slate-dim">
                Remember, you must use an{" "}
                <a
                  href="https://bsky.app/settings/app-passwords"
                  className="text-blue-500 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  app password
                </a>{" "}
                to access Graze! This is different than your account password.
                App passwords are created in the bluesky app and meant to be
                used for authentication to 3rd party services.
              </p>
              <div className="flex justify-end gap-4">
                <Button
                  className="relative"
                  type="submit"
                  isLoading={mutation.isPending}
                >
                  Login
                </Button>
              </div>

              {mutation.error && (
                <div className="text-red-500 text-sm">
                  {mutation.error.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
      <MarketingFooter />
    </main>
  );
}
